import React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public page: user lands here from the verification email link with ?token=...
 * Posts the token to /auth/verify-email, then shows success / error and offers
 * a CTA back to the app (signed-in users land on Dashboard, otherwise Home).
 */
export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [state, setState] = React.useState(token ? "verifying" : "missing");
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        await axios.post(`${API}/auth/verify-email`, { token });
        if (!cancelled) setState("ok");
      } catch (err) {
        if (cancelled) return;
        setError(err?.response?.data?.detail || "Verification failed. The link may be invalid or expired.");
        setState("err");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const hasSession = !!localStorage.getItem("vv_user");

  return (
    <div className="min-h-screen bg-[#F4F2EC] text-black flex items-center justify-center px-5 py-12">
      <div className="brutal-card bg-white p-8 sm:p-10 max-w-md w-full" data-testid="verify-email-card">
        <Link to="/" className="font-mono text-xs uppercase tracking-widest text-[#C2410C]">← Vendor Vault</Link>
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A] mt-6">Account</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black uppercase mt-1">Email verification</h1>

        {state === "missing" && (
          <div className="mt-8" data-testid="verify-state-missing">
            <div className="flex items-center gap-3 text-[#B00020]">
              <XCircle size={22}/>
              <span className="font-mono text-sm">No token in this link.</span>
            </div>
            <p className="font-mono text-xs text-[#4A4A4A] mt-3 leading-relaxed">
              Open the verification email and tap the button inside, or sign in and request a new link.
            </p>
          </div>
        )}

        {state === "verifying" && (
          <div className="mt-8 flex items-center gap-3" data-testid="verify-state-loading">
            <Loader2 size={22} className="animate-spin text-[#C2410C]"/>
            <span className="font-mono text-sm uppercase tracking-widest">Verifying…</span>
          </div>
        )}

        {state === "ok" && (
          <div className="mt-8" data-testid="verify-state-ok">
            <div className="flex items-center gap-3 text-[#4A7C59]">
              <CheckCircle2 size={22}/>
              <span className="font-mono text-sm uppercase tracking-widest">Email verified</span>
            </div>
            <p className="font-mono text-xs text-[#4A4A4A] mt-3 leading-relaxed">
              You're all set — thanks for confirming. You can close this tab or jump straight into the app.
            </p>
            <button
              type="button"
              onClick={() => navigate(hasSession ? "/dashboard" : "/")}
              className="brutal-btn mt-6 w-full"
              data-testid="verify-cta"
            >
              {hasSession ? "Go to dashboard →" : "Go to sign in →"}
            </button>
          </div>
        )}

        {state === "err" && (
          <div className="mt-8" data-testid="verify-state-err">
            <div className="flex items-center gap-3 text-[#B00020]">
              <XCircle size={22}/>
              <span className="font-mono text-sm uppercase tracking-widest">Verification failed</span>
            </div>
            <p className="font-mono text-xs text-[#4A4A4A] mt-3 leading-relaxed" data-testid="verify-err-detail">
              {error}
            </p>
            <p className="font-mono text-xs text-[#4A4A4A] mt-3 leading-relaxed">
              Sign in and we'll send a fresh link from your profile.
            </p>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="brutal-btn brutal-btn-secondary mt-6 w-full"
              data-testid="verify-cta-back"
            >
              Back to sign in
            </button>
          </div>
        )}

        <div className="mt-10 pt-6 border-t-2 border-black flex flex-wrap gap-4 font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A]">
          <Link to="/terms" className="underline">Terms</Link>
          <Link to="/privacy" className="underline">Privacy</Link>
          <a href="mailto:support@vendorvaultonline.com" className="underline">Support</a>
        </div>
      </div>
    </div>
  );
}
